import {
  MouseEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  MachinesListSupportSessionsStatusTypes,
  MachinesListSupportSessionsZitadelRoleTypes,
} from "@common/types/machines-list-types";
import { MachinesListMachineTypes } from "@src/types/gateways-with-machines";
import {
  updateModalsCloseDrawerModal,
  updateMachineInformationForRequestRemoteAccessDrawerModal,
  updateConnectionView,
  updateMaximumNumberOfMonitoredItems,
  updateNodeManagerActiveView,
} from "@src/reducers/your-reducer";
import { RootState } from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { machinesMergeSupportSessionsListData } from "@src/cloud-connect/utils/machines-merge-support-sessions-list-data";
import { setAllMachinesWithGatewaysListData } from "@src/reducers/gateways-machines-slice";
import { getVPNSingleUserUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-get-single-user-use-case";
import { postVPNInterruptSupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-interrupt-support-session-use-case";
import { postVPNCancelSupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-cancel-support-session-use-case";
import { postVPNDenySupportSessionUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-post-deny-support-session-use-case";
import { Route } from "@src/routes/routes";
import navigateTo from "@src/routes/navigator";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import { getNodeManager } from "@src/cloud-connect/dependency-injection-container/get-node-manager";
import { handleActiveSupportSessionButtonClick } from "./handle-active-support-session-button-click";
import { getSessionIdentityFlags } from "@src/cloud-connect/vpn-manager/utils/session-identity-flags";
import type { VncSettings } from "@src/cloud-connect/vnc/utils/novnc-loader";
import { getMachineActionDisabledReason } from "@src/cloud-connect/gateways-with-machines/entities/machine-action-state";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

export const useMachineListRowHook = (machine: MachinesListMachineTypes) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [showSTCancelModal, setShowSTCancelModal] = useState<boolean>(false);
  const [showOTCancelModal, setShowOTCancelModal] = useState<boolean>(false);
  const [stRequestClickLoading, setStRequestClickLoading] =
    useState<boolean>(false);
  const [otRequestClickLoading, setOtRequestClickLoading] =
    useState<boolean>(false);
  const [stActiveClickLoading, setStActiveClickLoading] =
    useState<boolean>(false);
  const [otActiveClickLoading, setOtActiveClickLoading] =
    useState<boolean>(false);
  const [disconnecting, setDisconnecting] = useState<boolean>(false);
  const [
    confirmCancelRemoteAccessRequestLoading,
    setConfirmCancelRemoteAccessRequestLoading,
  ] = useState<boolean>(false);
  const [localRemainingDuration, setLocalRemainingDuration] = useState<
    number | null
  >(null);
  const [isClosingState, setIsClosingState] = useState<boolean>(false);

  const [displayName, setDisplayName] = useState<string>("");

  // Track last processed event to avoid duplicate handling
  const lastProcessedEventRef = useRef<number | null>(null);

  const allMachinesListData = useSelector(
    (state: RootState) => state.gatewaysMachinesSlice.allMachinesListData,
    shallowEqual,
  );

  const organization = useSelector(
    (state: RootState) => state.yourSlice.authContext.organization,
  );

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const isRemoteActionsDisabled = useSSEUnavailable("service-vpn-manager");

  // SSE selectors for machine-specific event handling
  const lastEventAt = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["lastEventAt"],
  );

  const lastEvent = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["lastEvent"],
  );

  type SupportSessionEvent = {
    action?: string;
    session_id?: string;
    machine_id?: string;
  };

  const selectVpnSupportSessionEvent = (
    state: RootState,
  ): SupportSessionEvent | undefined =>
    state.sseSlice.streams?.["service-vpn-manager"]?.events?.[
      "support_session"
    ] as SupportSessionEvent | undefined;

  const supportSessionEventData = useSelector(selectVpnSupportSessionEvent);

  const goToNodeManager = () =>
    navigateTo(`${Route.NODE_MANAGER_PAGE}?origin=machines`);

  const showDisconnectSupportModal = useSelector(
    (state: RootState) =>
      state.yourSlice.modals.machinesCancelRequestDisconnectSupportModal
        .showModal,
  );

  const isConnected = machine.connection_state === "CONNECTED";

  const texts = {
    sourceIconAltText: i18n("modules.machinesListRow.sourceIconAltText"),
    noIpAddress: i18n("modules.machinesListRow.noIpAddress"),
    noSourceType: i18n("modules.machinesListRow.noSourceType"),
    viewSourceInfo: i18n("modules.machinesListRow.viewSourceInfo"),
    goToDashboard: i18n("modules.machinesListRow.goToDashboard"),
    goToConditionMonitoring: i18n(
      "modules.machinesListRow.goToConditionMonitoring",
    ),
    incomingRequested: i18n("modules.machinesListRow.incomingRequested"),
    active: i18n("modules.machinesListRow.active"),
    noGateway: i18n("modules.machinesListRow.noGateway"),
    moreOptions: i18n("modules.machinesListRow.moreOptions"),
    noSource: i18n("modules.machinesListRow.noSource"),
    request: i18n("modules.machinesListRow.request"),
    requested: i18n("modules.machinesListRow.requested"),
    closing: i18n("modules.machinesListRow.closing"),
    goToTreeStructure: i18n("modules.machinesListRow.goToTreeStructure"),
    noVendorId: i18n("modules.machinesListRow.noVendorId"),
    noMachineId: i18n("modules.machinesListRow.noMachineId"),
    copyTooltip: i18n("modules.machinesListRow.copyTooltip"),
    successMessage: i18n("modules.machinesListRow.successMessage"),
    errorMessage: i18n("modules.machinesListRow.errorMessage"),
    machineId: i18n("modules.machinesListRow.machineId"),
    goToMachineSettings: i18n("modules.machinesListRow.goToMachineSettings"),
    machineConfigurationWarning: i18n(
      "modules.machinesListRow.machineConfigurationWarning",
    ),
    missingMachineSkillTooltip: i18n(
      "modules.machinesListRow.missingMachineSkillTooltip",
    ),
    goToRequestTechnicalSupport: i18n(
      "modules.ticketSupportForMachineRowIconButton.goToRequestTechnicalSupport",
    ),
    goToShotDataTable: i18n("modules.machinesListRow.goToShotDataTable"),
    inProgressPrivate: i18n("modules.machinesListRow.inProgressPrivate"),
    inProgressSession: i18n("modules.machinesListRow.inProgressSession"),
    joinSession: i18n("modules.machinesListRow.joinSession"),
    inProgressPrivateTooltip: i18n(
      "modules.machinesListRow.inProgressPrivateTooltip",
    ),
    joinSessionTooltip: i18n("modules.machinesListRow.joinSessionTooltip"),
    viewing: i18n("modules.machinesListRow.viewing"),
    viewingTooltip: i18n("modules.machinesListRow.viewingTooltip"),
    reconnectingActionsUnavailableTooltip: i18n(
      "modules.sse.reconnectingActionsUnavailableTooltip",
    ),
  };

  const remoteActionsDisabledTooltip = isRemoteActionsDisabled
    ? texts.reconnectingActionsUnavailableTooltip
    : "";

  const modalTexts = {
    title: i18n("modules.machinesCancelRequestCrossModal.title"),
    message: i18n("modules.machinesCancelRequestCrossModal.message"),
    confirmButton: i18n(
      "modules.machinesCancelRequestCrossModal.confirmButton",
    ),
    cancelButton: i18n("modules.machinesCancelRequestCrossModal.cancelButton"),
  };

  const disconnectTexts = {
    title: i18n(`modules.machinesCancelRequestDisconnectSupportModal.title`),
    message: i18n(
      `modules.machinesCancelRequestDisconnectSupportModal.message`,
    ),
    confirmButton: i18n(
      `modules.machinesCancelRequestDisconnectSupportModal.confirmButton`,
    ),
    cancelButton: i18n(
      `modules.machinesCancelRequestDisconnectSupportModal.cancelButton`,
    ),
  };

  const isSessionActiveOrOpen =
    machine.status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
    machine.status === MachinesListSupportSessionsStatusTypes.OPEN;

  const isDurationExpired =
    isSessionActiveOrOpen &&
    (localRemainingDuration === 0 || machine?.remaining_duration === null);

  const {
    isCurrentUserSessionOwner,
    isCurrentUserSessionViewer,
    isCurrentUserSessionApprover,
    isManagerOwner,
    canRequestorCancelActiveInProgress,
    canApproverCancelActiveInProgress,
  } = getSessionIdentityFlags(machine, authContext);

  useEffect(() => {
    if (isDurationExpired) {
      setIsClosingState(true);
    }

    if (!isSessionActiveOrOpen) {
      setLocalRemainingDuration(null);
      setIsClosingState(false);
    }
  }, [isDurationExpired, isSessionActiveOrOpen]);

  // Handle SSE events for this specific machine
  useEffect(() => {
    if (!lastEventAt || !supportSessionEventData) return;
    if (lastEvent !== "support_session") return;
    if (lastEventAt === lastProcessedEventRef.current) return;
    const supportSessionMachineId = supportSessionEventData.machine_id;
    const belongsToMachine = supportSessionMachineId === machine.machine_id;

    if (!belongsToMachine) return;

    lastProcessedEventRef.current = lastEventAt;
    // Reset closing state when session is closed/expired for this machine
    if (supportSessionEventData.action === "closed_due_to_expiration") {
      // to ensure the UI updates immediately when a session expires, avoid flickering
      const mergedData = machinesMergeSupportSessionsListData({
        allMachinesListData,
        supportSessionsPayload: {
          machine_id: machine.machine_id,
          status: MachinesListSupportSessionsStatusTypes.INITIAL,
          remaining_duration: null,
        },
        isPostSupportSessionResponse: true,
      });

      if (mergedData) {
        dispatch(setAllMachinesWithGatewaysListData(mergedData));
      }

      setLocalRemainingDuration(null);
      setIsClosingState(false);
    }
  }, [
    allMachinesListData,
    dispatch,
    lastEventAt,
    lastEvent,
    machine.machine_id,
    supportSessionEventData,
  ]);

  const fetchTechnicianDisplayName = useCallback(async () => {
    const singleUserResponse = await getVPNSingleUserUseCase(
      machine?.technician_user_id,
      machine?.technician_namespace,
    );
    if (!singleUserResponse.success && !singleUserResponse.data?.display_name) {
      errorSnackbar(singleUserResponse.error);
      return;
    }

    if (singleUserResponse.success) {
      setDisplayName(singleUserResponse.data?.display_name ?? "");
      return singleUserResponse.data?.display_name ?? "";
    }
  }, [machine?.technician_user_id, machine?.technician_namespace]);

  const handleRequestButtonClick = async (
    e: MouseEvent | KeyboardEvent,
    userType: MachinesListSupportSessionsZitadelRoleTypes,
  ) => {
    e.stopPropagation();
    if (isRemoteActionsDisabled) return;
    if (
      userType === MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN &&
      machine.status !== MachinesListSupportSessionsStatusTypes.REQUESTED &&
      !machine.isRequested &&
      !machine.machine_id?.trim()
    ) {
      return;
    }

    (userType === MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN
      ? setStRequestClickLoading
      : setOtRequestClickLoading)(true);
    let userName = "";
    if (machine.status === MachinesListSupportSessionsStatusTypes.REQUESTED) {
      userName = await fetchTechnicianDisplayName();
    }

    dispatch(
      updateMachineInformationForRequestRemoteAccessDrawerModal({
        machineId: machine.machine_id,
        id: machine?.id,
        gatewayId: machine?.gateway_id,
        sourceName: machine?.name,
        companyName: organization,
        dsn: machine?.dsn,
        namespace: machine?.namespace,
        status: machine?.status,
        requested_duration: machine?.requested_duration,
        visibility: machine?.visibility,
        display_name: userName,
        requestMessage: machine?.message,
        userType: userType,
      }),
    );

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "machinesRequestRemoteMachineAccessDrawerModal",
      }),
    );
    (userType === MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN
      ? setStRequestClickLoading
      : setOtRequestClickLoading)(false);
  };

  const enterScreensharing = useCallback(
    async (
      userType: MachinesListSupportSessionsZitadelRoleTypes,
      settings?: VncSettings,
    ) => {
      if (isRemoteActionsDisabled) return;

      const setLoading =
        userType === MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN
          ? setStActiveClickLoading
          : setOtActiveClickLoading;

      await handleActiveSupportSessionButtonClick({
        setLoading,
        machine,
        isConnected,
        novncVersion: settings?.version,
        swapRB: settings?.swapRB,
      });
    },
    [isConnected, isRemoteActionsDisabled, machine],
  );

  const handleOTActiveButtonClick = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      e.stopPropagation();
      if (isRemoteActionsDisabled) return;
      enterScreensharing(
        MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
      );
    },
    [enterScreensharing, isRemoteActionsDisabled],
  );

  const handleViewSourceInfo = useCallback((e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const openSTCancelRequest = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      e.stopPropagation();
      if (isRemoteActionsDisabled) return;
      setShowSTCancelModal(true);
    },
    [isRemoteActionsDisabled],
  );

  const openOTCancelRequest = useCallback(
    async (e: MouseEvent | KeyboardEvent) => {
      e.stopPropagation();
      if (isRemoteActionsDisabled) return;
      await fetchTechnicianDisplayName();
      setShowOTCancelModal(true);
    },
    [fetchTechnicianDisplayName, isRemoteActionsDisabled],
  );

  const applyMergedSupportSessionsData = useCallback(
    (supportSessionsPayload) => {
      const mergedData = machinesMergeSupportSessionsListData({
        allMachinesListData,
        supportSessionsPayload,
        isPostSupportSessionResponse: true,
      });

      if (mergedData) {
        dispatch(setAllMachinesWithGatewaysListData(mergedData));
      }
    },
    [allMachinesListData, dispatch],
  );

  const confirmCancelRemoteAccessRequest = useCallback(
    async (e: MouseEvent | KeyboardEvent) => {
      e.stopPropagation();
      if (isRemoteActionsDisabled) return;
      setConfirmCancelRemoteAccessRequestLoading(true);

      if (!machine) {
        setShowOTCancelModal(false);
        return;
      }
      const cancelOrInterruptSupportSessionUseCase =
        machine?.status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
        machine?.status === MachinesListSupportSessionsStatusTypes.OPEN ||
        machine?.status === MachinesListSupportSessionsStatusTypes.IN_PROGRESS
          ? postVPNInterruptSupportSessionUseCase
          : postVPNCancelSupportSessionUseCase;

      try {
        const res = await cancelOrInterruptSupportSessionUseCase({
          namespace: machine?.namespace,
          id: machine?.id,
        });

        if (!res.success) {
          errorSnackbar(res?.error);
          setShowOTCancelModal(false);
          return;
        }

        applyMergedSupportSessionsData(res.data);
      } catch (error) {
        console.error("Error cancelling VPN request:", error);
      } finally {
        setShowOTCancelModal(false);
        setShowSTCancelModal(false);
        setConfirmCancelRemoteAccessRequestLoading(false);
      }
    },
    [machine, applyMergedSupportSessionsData, isRemoteActionsDisabled],
  );

  const confirmInterruptRemoteAccessRequest = useCallback(
    async (e: MouseEvent | KeyboardEvent) => {
      e.stopPropagation();
      if (isRemoteActionsDisabled) return;
      setConfirmCancelRemoteAccessRequestLoading(true);

      if (!machine) {
        setShowOTCancelModal(false);
        return;
      }
      const denyOrInterruptSupportSessionUseCase =
        machine?.status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
        machine?.status === MachinesListSupportSessionsStatusTypes.OPEN ||
        machine?.status === MachinesListSupportSessionsStatusTypes.IN_PROGRESS
          ? postVPNInterruptSupportSessionUseCase
          : postVPNDenySupportSessionUseCase;

      try {
        const res = await denyOrInterruptSupportSessionUseCase({
          namespace: machine?.namespace,
          id: machine?.id,
        });

        if (!res.success) {
          errorSnackbar(res?.error);
          setShowOTCancelModal(false);
          return;
        }

        applyMergedSupportSessionsData(res.data);
      } catch (error) {
        console.error("Error cancelling VPN request:", error);
      } finally {
        setShowOTCancelModal(false);
        setShowSTCancelModal(false);
        setConfirmCancelRemoteAccessRequestLoading(false);
      }
    },
    [machine, applyMergedSupportSessionsData, isRemoteActionsDisabled],
  );

  const closeOTCancelModal = useCallback((e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    setShowOTCancelModal(false);
  }, []);
  const closeSTCancelModal = useCallback((e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    setShowSTCancelModal(false);
  }, []);

  const closeDisconnectSupportModal = useCallback(() => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "machinesCancelRequestDisconnectSupportModal",
      }),
    );
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "machinesRequestRemoteMachineAccessDrawerModal",
      }),
    );
  }, [dispatch]);

  const handleSTActiveButtonClick = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      e.stopPropagation();
      if (isRemoteActionsDisabled) return;
      enterScreensharing(
        MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
      );
    },
    [enterScreensharing, isRemoteActionsDisabled],
  );

  const disconnectActiveSession = useCallback(async () => {
    if (isRemoteActionsDisabled || !machine) return;
    setDisconnecting(true);
    try {
      const res = await postVPNInterruptSupportSessionUseCase({
        namespace: machine?.namespace,
        id: machine?.id,
      });

      if (!res.success) {
        errorSnackbar(res?.error);
        return;
      }

      applyMergedSupportSessionsData(res.data);
    } catch (error) {
      console.error("Error disconnecting VPN session:", error);
      errorSnackbar(error instanceof Error ? error.message : undefined);
    } finally {
      setDisconnecting(false);
    }
  }, [machine, applyMergedSupportSessionsData, isRemoteActionsDisabled]);
  const writeSourceInfo = (machine) => {
    const sourceWithAuthInfo = {
      ...machine,
      // add default auth_info if not present
      auth_info: machine?.auth_info || {
        security_policy: "None",
        security_mode: "None",
        username: "",
        password: "",
      },
    };

    dispatch(
      updateConnectionView({
        source: {
          ...sourceWithAuthInfo,
          source_id: machine.opc_ua_data_source_id || "",
        },
        state: machine?.connection_state,
        authentication: DeviceAuthOption.ANONYMOUS,
      }),
    );

    dispatch(
      updateMaximumNumberOfMonitoredItems(
        machine.limits.maximum_number_of_monitored_items,
      ),
    );
  };

  const navigateToNodeManagerSection = (link) => {
    getNodeManager().flushAlarmRowSidePanelInfo();
    dispatch(updateNodeManagerActiveView(link));
    goToNodeManager();
  };

  const clickTreeStructureLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("treeStructure");
  };

  const clickDashboardLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("dashboard");
  };

  const clickConditionMonitoringLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("conditionMonitoring");
  };

  const clickMachineSettingsLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("machineSettings");
  };

  const clickShotDataTableLink = (e, machine) => {
    e.stopPropagation();
    writeSourceInfo(machine);
    navigateToNodeManagerSection("shotDataTable");
  };

  const isUserConditionMonitoringViewer = (): boolean => {
    const allowedRoles = [
      "view-condition-monitoring",
      "manage-condition-monitoring",
    ];

    return authContext?.permissions?.some((r) => allowedRoles.includes(r));
  };

  const isMachineProxy = machine.source_type === "MACHINE_V2_PROXY";
  const hasOpcUaSkill = isMachineProxy
    ? Boolean(machine.has_opc_ua_skill)
    : true;
  const hasVncSkill = isMachineProxy ? Boolean(machine.has_vnc_skill) : true;
  const actionDisabledReason = getMachineActionDisabledReason(machine);
  const showOTA = useFeatureFlag("show-ota", false);
  const showShotDataTable = useFeatureFlag("shotdata-table", false);

  return {
    isConnected,
    isRemoteActionsDisabled,
    remoteActionsDisabledTooltip,
    isClosingState,
    isDurationExpired,
    setLocalRemainingDuration,
    texts,
    modalTexts,
    showSTCancelModal,
    showOTCancelModal,
    showDisconnectSupportModal,
    handleRequestButtonClick,
    handleViewSourceInfo,
    openSTCancelRequest,
    confirmCancelRemoteAccessRequest,
    confirmInterruptRemoteAccessRequest,
    closeOTCancelModal,
    closeSTCancelModal,
    stRequestClickLoading,
    otRequestClickLoading,
    confirmCancelRemoteAccessRequestLoading,
    handleOTActiveButtonClick,
    stActiveClickLoading,
    otActiveClickLoading,
    closeDisconnectSupportModal,
    disconnectTexts,
    openOTCancelRequest,
    handleSTActiveButtonClick,
    displayName,
    isCurrentUserSessionOwner,
    isCurrentUserSessionViewer,
    isCurrentUserSessionApprover,
    isManagerOwner,
    canRequestorCancelActiveInProgress,
    canApproverCancelActiveInProgress,
    clickTreeStructureLink,
    clickDashboardLink,
    clickConditionMonitoringLink,
    isUserConditionMonitoringViewer,
    clickMachineSettingsLink,
    clickShotDataTableLink,
    enterScreensharing,
    disconnectActiveSession,
    disconnecting,
    hasOpcUaSkill,
    hasVncSkill,
    actionDisabledReason,
    showOTA,
    showShotDataTable,
  };
};
